import { useEffect, useRef, useState } from 'react';

/**
 * Tracks rows whose "signature" (e.g. an order status) changed since the last
 * time they were observed, and keeps them flagged for a short while so the UI
 * can apply a subtle highlight.
 *
 * The last-known signatures are kept at module scope per `registryKey`, so a
 * change that happened on another page (or another tab) is still highlighted
 * when the user comes back. Nothing is persisted to a backend or storage, and
 * the very first observation never highlights anything.
 */
const baselines = new Map<string, Map<string, string>>();

export function useChangedRowHighlight<T>(
  items: T[],
  getKey: (item: T) => string,
  getSignature: (item: T) => string,
  registryKey: string,
  duration = 1800,
): Set<string> {
  const keyRef = useRef(getKey);
  keyRef.current = getKey;
  const signatureRef = useRef(getSignature);
  signatureRef.current = getSignature;

  const [highlighted, setHighlighted] = useState<Set<string>>(new Set());
  const timersRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const next = new Map<string, string>();
    items.forEach((item) => {
      next.set(keyRef.current(item), signatureRef.current(item));
    });

    const baseline = baselines.get(registryKey);
    if (!baseline) {
      baselines.set(registryKey, new Map(next));
      return;
    }

    const changed: string[] = [];
    next.forEach((signature, key) => {
      const previous = baseline.get(key);
      if (previous !== undefined && previous !== signature) {
        changed.push(key);
      }
    });

    // Merge (instead of replace) so rows that leave the current view keep their
    // last-known signature and are still detected if their status changed.
    const merged = new Map(baseline);
    next.forEach((signature, key) => merged.set(key, signature));
    baselines.set(registryKey, merged);

    if (changed.length === 0) return;

    setHighlighted((previous) => {
      const updated = new Set(previous);
      changed.forEach((key) => updated.add(key));
      return updated;
    });

    changed.forEach((key) => {
      const existing = timersRef.current.get(key);
      if (existing) window.clearTimeout(existing);
      const timer = window.setTimeout(() => {
        setHighlighted((previous) => {
          if (!previous.has(key)) return previous;
          const updated = new Set(previous);
          updated.delete(key);
          return updated;
        });
        timersRef.current.delete(key);
      }, duration);
      timersRef.current.set(key, timer);
    });
  }, [items, registryKey, duration]);

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => window.clearTimeout(timer));
      timersRef.current.clear();
    },
    [],
  );

  return highlighted;
}